import { KeyboardEvent } from 'react';
import { DraftEditorCommand, getDefaultKeyBinding, KeyBindingUtil } from 'draft-js';

export type TranscriptEditorCommand =
  | (
      | 'transcript-editor-move-back'
      | 'transcript-editor-move-forward'
      | 'transcript-editor-pause-or-play'
      | 'transcript-editor-jump'
      | 'transcript-editor-capitalize-uppercase'
      | 'transcript-editor-capitalize-lowercase'
    )
  | DraftEditorCommand;

export enum KeyboardCommand {
  DOWNLOAD = 'download',
  PLAYBACK = 'playback',
  JUMP = 'jump',
  RECORD = 'record',
  RESTART = 'restart',
  SAVE = 'save',
  SEEKBACK = 'seekBack',
  SEEKFORWARD = 'seekForward',
  LABELSPEAKER = 'labelSpeaker',
}

export interface KeyboardShortcut {
  hasCommandModifier: boolean;
  key: string;
}

export interface KeyboardShortcuts {
  [key: string]: KeyboardShortcut;
}

const defaultShortcuts: KeyboardShortcuts = {
  record: {
    hasCommandModifier: true,
    key: 'd',
  },
  download: {
    hasCommandModifier: true,
    key: 'x',
  },
  jump: {
    hasCommandModifier: true,
    key: ' ',
  },
  save: {
    hasCommandModifier: true,
    key: 's',
  },
  restart: {
    hasCommandModifier: true,
    key: 'o',
  },
  seekBack: {
    hasCommandModifier: true,
    key: 'ArrowLeft',
  },
  seekForward: {
    hasCommandModifier: true,
    key: 'ArrowRight',
  },
  playback: {
    hasCommandModifier: false,
    key: 'Tab',
  },
  capitalize: {
    hasCommandModifier: true,
    key: 'ArrowUp',
  },
  lowercase: {
    hasCommandModifier: true,
    key: 'ArrowDown',
  },
  labelSpeaker: {
    hasCommandModifier: true,
    key: 'l',
  },
};

interface Props {
  onDownload: () => void;
  onPlayback: () => void;
  onRecord: (command?: KeyboardCommand) => void;
  onRestart: () => void;
  onSave: () => void;
  onSeek: (back?: boolean) => void;
  shortcuts?: KeyboardShortcuts;
  onLabelSpeaker?: () => void;
}

export default class KeyboardUtils {
  private onDownload: () => void;
  private onPlayback: () => void;
  private onRecord: () => void;
  private onRestart: () => void;
  private onSave: () => void;
  private onSeek: (back?: boolean) => void;
  private onLabelSpeaker?: () => void;
  shortcuts: KeyboardShortcuts;

  private setShortcutCommand!: KeyboardCommand;
  private setShortcutCallback!: () => void;

  constructor(props: Props) {
    this.onDownload = props.onDownload;
    this.onPlayback = props.onPlayback;
    this.onRecord = props.onRecord;
    this.onRestart = props.onRestart;
    this.onSave = props.onSave;
    this.onSeek = props.onSeek;
    this.onLabelSpeaker = props.onLabelSpeaker;
    this.shortcuts = this.loadFromLocal() || { ...defaultShortcuts };
  }

  private handleGlobalShortcuts = (event: KeyboardEvent) => {
    const commandModifier = KeyBindingUtil.hasCommandModifier(event);

    const shortcut = Object.keys(this.shortcuts).find((value: string) => {
      const { hasCommandModifier, key } = this.shortcuts[value];
      return event.key === key && commandModifier === hasCommandModifier;
    });

    if (shortcut) {
      event.preventDefault();
      switch (shortcut) {
        case 'record':
          // Start/pause dictation
          return this.onRecord();
        case 'download':
          // Copy all contents
          return this.onDownload();
        case 'save':
          // Finish and save
          return this.onSave();
        case 'restart':
          // Start new
          return this.onRestart();
        case 'seekBack':
          // Move back 2 seconds
          return this.onSeek(true);
        case 'seekForward':
          // Move forward 2 seconds
          return this.onSeek();
        case 'playback':
          // Play/pause
          return this.onPlayback();
        case 'labelSpeaker':
          // Begin / end marking speaker
          return this.onLabelSpeaker ? this.onLabelSpeaker() : null;
        default:
          return;
      }
    }
  };

  private loadFromLocal = () => {
    return JSON.parse(localStorage.getItem('keyboard-utils-shortcuts') as string) || undefined;
  };

  private saveToLocal = () => {
    localStorage.setItem('keyboard-utils-shortcuts', JSON.stringify(this.shortcuts));
  };

  private getShortcut = (event: KeyboardEvent): KeyboardShortcut | undefined => {
    const hasCommandModifier = KeyBindingUtil.hasCommandModifier(event);
    if (hasCommandModifier && (event.key === 'Meta' || event.key === 'Control')) {
      return;
    } else {
      return {
        hasCommandModifier,
        key: event.key,
      };
    }
  };

  private getValueForDuplicate = (
    command: KeyboardCommand,
    shortcut: KeyboardShortcut,
  ): { key: string; shortcut: KeyboardShortcut } | undefined => {
    const key = Object.keys(this.shortcuts).find((value: string) => {
      const obj = this.shortcuts[value];
      return obj.key === shortcut.key && obj.hasCommandModifier === shortcut.hasCommandModifier;
    });
    if (!key) {
      return;
    }
    const defaultValue = defaultShortcuts[key];

    if (defaultValue.key === shortcut.key && defaultValue.hasCommandModifier === shortcut.hasCommandModifier) {
      // If new shortcut is overriding a default value, swap
      return { key, shortcut: this.shortcuts[command.toString()] };
    } else {
      return { key, shortcut: defaultValue };
    }
  };

  private handleSetShortcut = (event: KeyboardEvent) => {
    event.preventDefault();
    const newShortcut = this.getShortcut(event);
    if (newShortcut) {
      const duplicate = this.getValueForDuplicate(this.setShortcutCommand, newShortcut);
      if (duplicate) {
        this.shortcuts[duplicate.key] = duplicate.shortcut;
      }
      this.shortcuts[this.setShortcutCommand] = newShortcut;
      this.setShortcutCallback();
      this.saveToLocal();
      this.finishSettingShortcut();
    }
  };

  private startSettingShortcut = () => {
    this.deregisterShortcutListeners();
    window.addEventListener('keydown', this.handleSetShortcut as (event: any) => void);
  };

  private finishSettingShortcut = () => {
    window.removeEventListener('keydown', this.handleSetShortcut as (event: any) => void);
    this.registerShortcutListeners();
  };

  resetDefaults = () => {
    this.shortcuts = { ...defaultShortcuts };
    this.saveToLocal();
  };

  getShortcuts = () => this.shortcuts;

  setShortcut = (command: KeyboardCommand, callback: () => void) => {
    this.setShortcutCommand = command;
    this.setShortcutCallback = callback;
    this.startSettingShortcut();
  };

  registerShortcutListeners = () => {
    console.debug('registering shortcut listeners');
    window.addEventListener('keydown', this.handleGlobalShortcuts as (event: any) => void);
    console.debug('successfully registered shortcut listeners');
  };

  deregisterShortcutListeners = () => {
    console.debug('deregistering shortcut listeners');
    window.removeEventListener('keydown', this.handleGlobalShortcuts as (event: any) => void);
    console.debug('successfully deregistered shortcut listeners');
  };

  draftKeyBindingFn = (event: KeyboardEvent): TranscriptEditorCommand | null => {
    const commandModifier = KeyBindingUtil.hasCommandModifier(event);

    const shortcut = Object.keys(this.shortcuts).find((value: string) => {
      const { hasCommandModifier, key } = this.shortcuts[value];
      return event.key === key && commandModifier === hasCommandModifier;
    });

    if (shortcut) {
      event.preventDefault();
      switch (shortcut) {
        case 'capitalize':
          return 'transcript-editor-capitalize-uppercase';
        case 'lowercase':
          return 'transcript-editor-capitalize-lowercase';
        case 'jump':
          return 'transcript-editor-jump';
        default:
          return 'transcript-editor-pause-or-play';
      }
    } else {
      return getDefaultKeyBinding(event);
    }

    /*         const commandModifier = KeyBindingUtil.hasCommandModifier(event);
                const shortcut = this.shortcuts['jump']; */

    /*         if (shortcut.hasCommandModifier == commandModifier && shortcut.key == event.key) {
            return "transcript-editor-pause-or-play";
        } else {

        } */
  };
}
